import { uid as getUid } from "@dhx/ts-common/core";
import { sv } from "@dhx/ts-common/dom";
import { calcPointRef, linearGradient, verticalCenteredText } from "../helpers/common";
import { IScale, ISeriaConfig, PointData, ScaleType, SvgElement, TooltipType } from "../types";
import ScaleSeria from "./ScaleSeria";

export default class Bar extends ScaleSeria {
	protected _shift = 0;
	protected _baseLinePosition: number;

	addScale(type: ScaleType, scale: IScale): void {
		super.addScale(type, scale);
		scale.addPadding();
	}

	seriesShift(shift: number): number {
		this._shift = shift;
		return this.config.barWidth;
	}

	paint(width: number, height: number, prev?: PointData[]): SvgElement {
		super.paint(width, height);
		const getChartAriaAttrs = cfg => ({
			"aria-label": `chart ${cfg.value || ""}`,
		});

		if (!this.config.active) {
			return null;
		}
		let svg = [];
		if (this._gradient) {
			svg.push(sv("defs", [this._gradient()]));
		}

		const css = `chart ${this.config.type} ${this.config.css || ""} ${
			this.config.dashed ? "dash-line" : ""
		}`;
		const form = this._getForm(this._points, css, width, height, prev);
		svg = svg.concat(form);
		return sv(
			"g",
			{
				class: "seria",
				_key: this.id,
				...getChartAriaAttrs(this.config),
				tabindex: 0,
			},
			svg
		);
	}

	getTooltipType(_id: string, _x?: number, y?: number): TooltipType {
		if (this.config.baseLine !== undefined && this._baseLinePosition < y) {
			return "bot";
		}
		return "top";
	}

	protected _getClosestDist(x, y, px, py) {
		if (this.config.stacked && y < py) {
			return Infinity;
		}
		return Math.abs(x - px);
	}

	protected _path(item, prev): string {
		item[0] += this._shift;
		return `
M ${item[0] - this.config.barWidth / 2} ${prev}
V ${item[1]}
h ${this.config.barWidth}
V ${prev}`;
	}

	protected _base(height) {
		const baseLine = this.config.baseLine;
		return (this._baseLinePosition =
			baseLine !== undefined ? this.yScale.point(baseLine) * height : height - 1);
	}

	protected _text(item, prev, rotate) {
		const x = item[0];
		const y = (prev + item[1]) / 2;
		const canRotate = rotate && !isNaN(rotate);
		return {
			x,
			y,
			class: "bar-text",
			transform: canRotate ? `rotate(${rotate} ${x} ${y})` : "",
		};
	}

	protected _getForm(
		points: PointData[],
		css: string,
		_width: number,
		height: number,
		prev: PointData[]
	): object {
		const getPointAriaLabel = (barType, item, baseLine = 0) => {
			const x = item[3];
			let yStart = baseLine;
			let yEnd = item[4];

			if (baseLine > yEnd) {
				yStart = yEnd;
				yEnd = baseLine;
			}

			return barType === "xbar"
				? `bar y=${x}, x from ${yStart} to ${yEnd}`
				: `bar x=${x}, y from ${yStart} to ${yEnd}`;
		};
		const getPointAriaAttrs = (barType, item, baseLine) => ({
			role: "graphics-symbol",
			"aria-roledescription": "bar",
			"aria-label": getPointAriaLabel(barType, item, baseLine),
		});
		const { baseLine, fill, alpha, showText, showTextTemplate, showTextRotate } = this.config;

		const svg = [];
		const base = this._base(height);
		const getPrev = index => (!prev ? base : prev[index][1]);

		const series = points.map((item, index) => {
			return sv("path", {
				_key: "seria" + this.config.id + index,
				_ref: calcPointRef(item[2], this.config.id),
				d: this._path(item, getPrev(index)),
				class: css,
				fill,
				onclick: [this._handlers.onclick, item[2], this.config.value],
				onmousemove: [this._handlers.onmousemove, item[2], this.config.id],
				onmouseleave: [this._handlers.onmouseleave, item[2], this.config.id],
				"fill-opacity": alpha,
				...getPointAriaAttrs(this.config.type, item, baseLine),
				tabindex: 0,
			});
		});
		svg.push(...series);

		if ((showText || showTextTemplate || showTextRotate) && showText !== false) {
			const isWrite = (item, index) => Math.abs(getPrev(index) - item[1]) > 16; // hide text, where height < 16
			const text = points.map((item, index) => {
				const value = this._getText(item);
				return isWrite(item, index)
					? sv(
							"text",
							{
								...this._text(item, getPrev(index), showTextRotate),
								"aria-hidden": "true",
							},
							[
								showTextTemplate
									? verticalCenteredText(showTextTemplate(value))
									: verticalCenteredText(value),
							]
					  )
					: null;
			});
			svg.push(...text);
		}
		return svg;
	}

	protected _getText(item) {
		return item[4].toString();
	}

	protected _setDefaults(config: ISeriaConfig) {
		const defaults = {
			barWidth: 30,
			alpha: 1,
			active: true,
			tooltip: true,
			pointType: "empty",
		};
		this.config = { ...defaults, ...config } as ISeriaConfig;
		const point = this.config.pointType;
		const color = this.config.pointColor || this.config.color;

		if (point) {
			this.config.pointType = point;
			this._drawPointType = this._getPointType(point, color);
		}
		if (this.config.gradient) {
			const id = "gradient" + getUid();
			const gradient = this.config.gradient(this.config.fill);
			this._gradient = () => linearGradient(gradient, id);
			this.config.fill = `url(#${id})`;
		}
	}
}
